// Control de acceso por sala en el frontend. Fuente: GET /me (participante). Se maneja
// como estado EXPLÍCITO (loading/ready/error) y FAIL-CLOSED: mientras carga no se muestran
// nav ni rutas; ante error no se muestra ninguna sala. `null` = todas; `undefined` jamás.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";
import type { Session } from "../types";
import { getMe } from "./users";
import { alExpirarSesion, sesionEstaExpirada, volverAlLogin } from "./auth";
import { isEnabled } from "./flags";
import { FINANZAS_EN_MENU, ALFA_EN_MENU, EMPLEO_EN_MENU, SOLICITUDES_EN_MENU } from "../config";
import { SALAS_CONTROLABLES, SALAS_KIOSCO, salaPermitida, primeraSalaDestino } from "./salas";
import { Cargando } from "../components/Cargando";
import { ROOMS, moduloEnSala } from "./content";

const AccessContext = createContext<string[] | null>(null);
const GradeContext = createContext<string | null>(null);
const NombreContext = createContext<string | null>(null);
/** Salas permitidas del participante (null = todas). Para staff siempre null. */
export function useAllowedRooms(): string[] | null {
  return useContext(AccessContext);
}
/** Grado del participante autenticado; null para staff/adultos sin grado escolar. */
export function useParticipantGrade(): string | null {
  return useContext(GradeContext);
}

/**
 * Nombre del participante autenticado, para saludarlo por su nombre.
 *
 * `null` cuando es staff (que ya se identifica con su usuario) o cuando el menor
 * se registró POR APARTAMENTO: en ese modo no se guarda el nombre a propósito, y
 * es la protección de privacidad la que manda, no la comodidad de la pantalla.
 */
export function useParticipantNombre(): string | null {
  return useContext(NombreContext);
}

/**
 * El participante no tiene NI UNA sala: o su residencial apagó su grado, o le
 * quitaron todos los módulos.
 *
 * Distingue `[]` (ninguna) de `null` (todas, cuentas legacy). Quien lo consuma
 * tiene que pintarlo DENTRO de Layout: devolverlo desde StudentGate sustituiría
 * la aplicación entera —AccessGate envuelve a Layout— y la persona se quedaría
 * sin navegación, sin perfil y sin poder cerrar sesión.
 */
export function useSinModulos(): boolean {
  const rooms = useContext(AccessContext);
  return rooms !== null && rooms.length === 0;
}

type AccessState =
  | { status: "loading" }
  | { status: "ready"; allowedRooms: string[] | null; grade: string | null; nombre: string | null }
  | { status: "error" };

/** Habilitación real de una sala (flags de menú + flags de contenido). */
function salaHabilitada(key: string): boolean {
  if (key === "finanzas") return FINANZAS_EN_MENU;
  if (key === "alfabetizacion") return ALFA_EN_MENU;
  if (key === "empleo") return EMPLEO_EN_MENU;
  if (key === "adultos") return SOLICITUDES_EN_MENU;
  return isEnabled(key);
}
function salasHabilitadas(): string[] {
  return (SALAS_CONTROLABLES as readonly string[]).filter(salaHabilitada);
}

export function AccessGate({ session, children }: { session: Session; children: ReactNode }) {
  /*
   * El kiosco: sus salas son una CONSTANTE, no vienen de `/me` —no tiene fila en
   * `participants` y `/me` exige rol `participant`—.
   *
   * Va ANTES de la rama de staff: ahí abajo se entrega `null`, que significa TODAS
   * las salas. Un rol nuevo que caiga ahí no recibe "ninguna", recibe "todas". Es
   * el mismo modo de fallo del PR #91.
   */
  if (session.role === "kiosk") {
    const suyas = (SALAS_KIOSCO as readonly string[]).filter(salaHabilitada);
    return (
      <NombreContext.Provider value={null}>
        <GradeContext.Provider value={null}>
          <AccessContext.Provider value={suyas}>{children}</AccessContext.Provider>
        </GradeContext.Provider>
      </NombreContext.Provider>
    );
  }
  // Staff no tiene restricción por sala; su panel se renderiza directo (contexto = null).
  if (session.role !== "student") {
    return (
      <NombreContext.Provider value={null}>
        <GradeContext.Provider value={null}>
          <AccessContext.Provider value={null}>{children}</AccessContext.Provider>
        </GradeContext.Provider>
      </NombreContext.Provider>
    );
  }
  return <StudentGate session={session}>{children}</StudentGate>;
}

function StudentGate({ session, children }: { session: Session; children: ReactNode }) {
  const [state, setState] = useState<AccessState>({ status: "loading" });
  const [intento, setIntento] = useState(0);
  const [caida, setCaida] = useState(sesionEstaExpirada);
  useEffect(() => alExpirarSesion(() => setCaida(true)), []);
  useEffect(() => {
    let vivo = true;
    setState({ status: "loading" });
    getMe().then(({ profile, error }) => {
      if (!vivo) return;
      if (error || !profile) setState({ status: "error" });
      // `?? null` y NO `?? []`: durante la ventana de despliegue /me devuelve null
      // para las cuentas que todavía no tienen allowed_rooms, y eso significa
      // "todas". Convertirlo en [] las dejaría sin módulos justo al desplegar.
      else setState({
        status: "ready",
        allowedRooms: profile.allowedRooms ?? null,
        grade: profile.grade ?? null,
        // Registro por apartamento: /me devuelve el nombre en null a proposito.
        nombre: [profile.firstName, profile.lastName].filter(Boolean).join(" ") || null,
      });
    });
    return () => { vivo = false; };
  }, [session.user, intento]);

  if (state.status === "loading") return <div className="center-msg"><Cargando texto="Verificando tu acceso…" /></div>;
  if (state.status === "error") {
    /*
     * Este error se pinta SIN Layout —StudentGate lo envuelve—, así que aquí no
     * está el botón «Salir» ni el aviso de sesión caída. Si lo que falló fue la
     * sesión (401 que no se pudo renovar), «Reintentar» repite el mismo 401 para
     * siempre: hay que ofrecer volver a entrar. `volverAlLogin` hace el logout.
     */
    if (caida) {
      return (
        <div className="center-msg">
          <p>Tu sesión ya no es válida. Vuelve a entrar para seguir.</p>
          <button className="btn-primary" onClick={volverAlLogin}>Salir y volver a entrar</button>
        </div>
      );
    }
    return (
      <div className="center-msg">
        <p>No pudimos verificar tu acceso.</p>
        <button className="btn-primary" onClick={() => setIntento((i) => i + 1)}>Reintentar</button>
      </div>
    );
  }
  return (
    <NombreContext.Provider value={state.nombre}>
      <GradeContext.Provider value={state.grade}>
        <AccessContext.Provider value={state.allowedRooms}>{children}</AccessContext.Provider>
      </GradeContext.Provider>
    </NombreContext.Provider>
  );
}

/** Envuelve la ruta de una sala controlable: si el usuario no la tiene permitida, redirige. */
export function SalaGuard({ sala, children }: { sala: string; children: ReactNode }) {
  const allowed = useAllowedRooms();
  if (!salaPermitida(sala, allowed)) {
    return <Navigate to={primeraSalaDestino(allowed, salasHabilitadas())} replace />;
  }
  return <>{children}</>;
}

/**
 * Para /:room/:slug: la sala tiene que estar permitida Y el módulo tiene que
 * pertenecer a esa sala (ROOMS en lib/content).
 *
 * Solo con la sala no bastaba: `salaPermitida` deja pasar cualquier clave que no
 * sea controlable (así funcionan las rutas utilitarias), de modo que
 * `#/cualquiera/matematicas-superior` entraba, y el kiosco abría
 * `#/stem/matematicas` porque `stem` sí es suya aunque no tenga módulos.
 */
export function SubjectDetailGuard({ children }: { children: ReactNode }) {
  const { room = "", slug = "" } = useParams();
  const allowed = useAllowedRooms();
  if (!moduloEnSala(room, slug)) {
    // Sala conocida pero módulo ajeno: a la sala (que pasa por su propia
    // guarda). Sala desconocida: al mismo destino que usa SalaGuard.
    if (Object.prototype.hasOwnProperty.call(ROOMS, room)) {
      return <SalaGuard sala={room}><Navigate to={`/${room}`} replace /></SalaGuard>;
    }
    return <Navigate to={primeraSalaDestino(allowed, salasHabilitadas())} replace />;
  }
  return <SalaGuard sala={room}>{children}</SalaGuard>;
}
